import React from 'react';
import styled from 'styled-components';

import { DataElementContext } from '@/page-components/common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';

type ScrollSpyProps = {
  children?: React.ReactNode;
  style?: React.CSSProperties;
  styleText: string;
  className: string;
  nodeId?: string;
  properties?: Record<string, any>;
  [key: string]: any;
};

type ScrollSpyItem = Record<string, any> & {
  id: string;
  label: string;
  targetId: string;
  selector: string;
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const parseNumber = (value: unknown, fallback = 0) => {
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) return parsed;
  }

  return fallback;
};

const parseBoolean = (value: unknown, fallback = false) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }

  return fallback;
};

const escapeCssValue = (value: string) => {
  try {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
      return CSS.escape(value);
    }
  } catch {
    /* noop */
  }

  return value.replace(/[^a-zA-Z0-9\-_]/g, '\\$&');
};

const isSelectorLike = (value: string) => {
  return (
    value.startsWith('#') ||
    value.startsWith('.') ||
    value.startsWith('[') ||
    value.includes(' ') ||
    value.includes('>') ||
    value.includes(':')
  );
};

const getElementForSelector = (selector?: string) => {
  if (!selector) return null;

  try {
    return document.querySelector(selector) as HTMLElement | null;
  } catch {
    return null;
  }
};

const ensureElementId = (element: HTMLElement, fallbackId: string) => {
  if (!element.id) {
    element.id = fallbackId;
  }

  return element.id;
};

const buildSelector = (value?: string) => {
  if (!value) return '';
  if (isSelectorLike(value)) return value;
  return `#${escapeCssValue(value)}`;
};

const getItemId = (item: Record<string, any>, index: number, keys: Record<string, string>) => {
  const rawId =
    item[keys.idKey] ??
    item[keys.targetIdKey] ??
    item.id ??
    item.targetId ??
    item[keys.selectorKey] ??
    `scroll-spy-${index}`;

  return String(rawId);
};

const normalizeExplicitItems = (
  list: Record<string, any>[],
  keys: Record<string, string>,
  autoLabelFromTarget: boolean,
) => {
  return list.map((item, index) => {
    const id = getItemId(item, index, keys);
    const explicitTargetId = item[keys.targetIdKey] ?? item.targetId ?? '';
    const explicitSelector = item[keys.selectorKey] ?? item.selector ?? '';
    const targetId = String(explicitTargetId || (isSelectorLike(id) ? '' : id)).replace(/^#/, '');
    const selector = buildSelector(explicitSelector || targetId || id);
    let label = item[keys.labelKey] ?? item.label ?? '';

    if (!label && autoLabelFromTarget && selector) {
      const targetElement = getElementForSelector(selector);
      label =
        targetElement?.getAttribute('data-scroll-spy-label') ??
        targetElement?.getAttribute('aria-label') ??
        targetElement?.textContent?.trim() ??
        '';
    }

    return {
      ...item,
      id,
      label: String(label ?? ''),
      targetId,
      selector,
    } as ScrollSpyItem;
  });
};

const normalizeSelectorItems = (selector: string, labelAttribute: string, generatedPrefix: string) => {
  let elements: HTMLElement[] = [];

  try {
    elements = Array.from(document.querySelectorAll(selector)) as HTMLElement[];
  } catch {
    return [] as ScrollSpyItem[];
  }

  return elements.map((element, index) => {
    const fallbackId = `${generatedPrefix}-${index}`;
    const targetId =
      element.getAttribute('data-scroll-spy-id') ?? element.getAttribute('id') ?? ensureElementId(element, fallbackId);

    return {
      id: targetId,
      label:
        element.getAttribute(labelAttribute) ??
        element.getAttribute('data-scroll-spy-label') ??
        element.getAttribute('aria-label') ??
        element.textContent?.trim() ??
        '',
      targetId,
      selector: `#${escapeCssValue(targetId)}`,
    };
  });
};

const getElementForItem = (item: ScrollSpyItem) => {
  return getElementForSelector(item.selector);
};

const ScrollSpy = (componentProps: ScrollSpyProps) => {
  let props = componentProps;
  let isVisible = true;

  const dataElementContext = React.useContext(DataElementContext);
  [props, isVisible] = processComponentProps(props, dataElementContext);

  const normalizedProperties = React.useMemo<Record<string, any>>(() => {
    const source = props.properties ?? {};
    const offset = parseNumber(source.offset, 0);

    return {
      ...source,
      autoLabelFromTarget: parseBoolean(source.autoLabelFromTarget, false),
      offset,
      observeMutations: parseBoolean(source.observeMutations, true),
      activationOffset: parseNumber(source.activationOffset, offset),
      scrollOffset: parseNumber(source.scrollOffset, offset),
      updateHash: parseBoolean(source.updateHash, false),
    };
  }, [props.properties]);

  const [activeId, setActiveId] = React.useState('');
  const [refreshTick, setRefreshTick] = React.useState(0);
  const refreshFrameRef = React.useRef<number | null>(null);

  const scheduleRefresh = React.useCallback(() => {
    if (refreshFrameRef.current != null) {
      window.cancelAnimationFrame(refreshFrameRef.current);
    }

    refreshFrameRef.current = window.requestAnimationFrame(() => {
      setRefreshTick((value) => value + 1);
      refreshFrameRef.current = null;
    });
  }, []);

  React.useEffect(() => {
    if (!isVisible) return;

    scheduleRefresh();

    const onWindowChange = () => {
      scheduleRefresh();
    };

    window.addEventListener('load', onWindowChange);
    window.addEventListener('resize', onWindowChange);
    window.addEventListener('orientationchange', onWindowChange);
    window.addEventListener('hashchange', onWindowChange);

    const shouldObserveMutations = normalizedProperties.observeMutations !== false;
    let mutationObserver: MutationObserver | null = null;

    if (shouldObserveMutations && document.body) {
      mutationObserver = new MutationObserver(() => {
        scheduleRefresh();
      });

      mutationObserver.observe(document.body, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['id', 'data-scroll-spy-id', 'data-scroll-spy-label'],
      });
    }

    return () => {
      window.removeEventListener('load', onWindowChange);
      window.removeEventListener('resize', onWindowChange);
      window.removeEventListener('orientationchange', onWindowChange);
      window.removeEventListener('hashchange', onWindowChange);

      if (mutationObserver) {
        mutationObserver.disconnect();
      }

      if (refreshFrameRef.current != null) {
        window.cancelAnimationFrame(refreshFrameRef.current);
        refreshFrameRef.current = null;
      }
    };
  }, [isVisible, normalizedProperties.observeMutations, scheduleRefresh]);

  const resolvedItems = React.useMemo(() => {
    if (!isVisible) return [] as ScrollSpyItem[];
    const refreshNonce = refreshTick;
    void refreshNonce;

    const list = Array.isArray(normalizedProperties.list) ? normalizedProperties.list : [];

    const keys = {
      idKey: normalizedProperties.idKey ?? 'id',
      labelKey: normalizedProperties.labelKey ?? 'label',
      targetIdKey: normalizedProperties.targetIdKey ?? 'targetId',
      selectorKey: normalizedProperties.selectorKey ?? 'selector',
    };

    if (list.length) {
      return normalizeExplicitItems(list, keys, normalizedProperties.autoLabelFromTarget === true);
    }

    const selector = normalizedProperties.selector ?? normalizedProperties.sectionsSelector ?? '';
    if (selector) {
      return normalizeSelectorItems(
        selector,
        normalizedProperties.labelAttribute ?? 'data-scroll-spy-label',
        props.nodeId ?? 'scroll-spy',
      );
    }

    return [] as ScrollSpyItem[];
  }, [isVisible, normalizedProperties, props.nodeId, refreshTick]);

  const updateHash = React.useCallback(
    (targetId: string) => {
      if (!normalizedProperties.updateHash || !targetId) return;

      const nextHash = `#${targetId}`;
      if (window.location.hash === nextHash) return;

      window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${window.location.search}${nextHash}`,
      );
    },
    [normalizedProperties.updateHash],
  );

  const scrollToItem = React.useCallback(
    (itemOrId: string | ScrollSpyItem, event?: React.MouseEvent<HTMLElement>) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      const item =
        typeof itemOrId === 'string'
          ? resolvedItems.find((entry) => entry.id === itemOrId || entry.targetId === itemOrId)
          : itemOrId;

      if (!item) return false;

      const element = getElementForItem(item);
      if (!element) return false;

      const top = window.scrollY + element.getBoundingClientRect().top - normalizedProperties.scrollOffset;
      const behavior = normalizedProperties.scrollBehavior ?? 'smooth';

      window.scrollTo({
        top: Math.max(top, 0),
        behavior,
      });

      setActiveId(item.id);
      updateHash(item.targetId);

      return true;
    },
    [normalizedProperties.scrollBehavior, normalizedProperties.scrollOffset, resolvedItems, updateHash],
  );

  React.useEffect(() => {
    if (!resolvedItems.length) {
      if (activeId !== '') {
        setActiveId('');
      }
      return;
    }

    const idFromHash = window.location.hash ? window.location.hash.replace(/^#/, '') : '';
    const initialActiveId =
      normalizedProperties.initialActiveId ??
      (idFromHash && resolvedItems.some((item) => item.targetId === idFromHash || item.id === idFromHash)
        ? idFromHash
        : '') ??
      resolvedItems[0]?.id;

    if (!activeId || !resolvedItems.some((item) => item.id === activeId)) {
      setActiveId(initialActiveId);
    }
  }, [activeId, normalizedProperties.initialActiveId, resolvedItems]);

  React.useEffect(() => {
    if (!resolvedItems.length) return;

    let frame: number | null = null;

    const syncActiveItem = () => {
      if (frame != null) return;

      frame = window.requestAnimationFrame(() => {
        frame = null;

        const sections = resolvedItems
          .map((item) => ({
            item,
            element: getElementForItem(item),
          }))
          .filter((entry) => entry.element != null) as { item: ScrollSpyItem; element: HTMLElement }[];

        if (!sections.length) return;

        let nextActiveId = sections[0].item.id;

        for (const section of sections) {
          const top = section.element.getBoundingClientRect().top;
          if (top <= normalizedProperties.activationOffset + 1) {
            nextActiveId = section.item.id;
          } else {
            break;
          }
        }

        setActiveId((currentValue) => {
          if (currentValue === nextActiveId) return currentValue;

          const nextItem = sections.find((section) => section.item.id === nextActiveId)?.item;
          if (nextItem) {
            updateHash(nextItem.targetId);
          }

          return nextActiveId;
        });
      });
    };

    syncActiveItem();

    window.addEventListener('scroll', syncActiveItem, { passive: true });
    window.addEventListener('resize', syncActiveItem);

    return () => {
      window.removeEventListener('scroll', syncActiveItem);
      window.removeEventListener('resize', syncActiveItem);

      if (frame != null) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [normalizedProperties.activationOffset, resolvedItems, updateHash]);

  const items = React.useMemo(() => {
    return resolvedItems.map((item, index) => ({
      ...item,
      index: index,
      length: resolvedItems.length,
      active: item.id === activeId,
      onClick: (event?: React.MouseEvent<HTMLElement>) => scrollToItem(item, event),
    }));
  }, [activeId, resolvedItems, scrollToItem]);

  const contextValue = React.useMemo(() => {
    const activeItem = items.find((item) => item.id === activeId) ?? null;

    return {
      activeId,
      activeItem,
      activeIndex: activeItem?.index ?? -1,
      items,
      list: items,
      onItemClick: scrollToItem,
      scrollToItem,
      refresh: scheduleRefresh,
      parentContext: dataElementContext,
    };
  }, [activeId, dataElementContext, items, scheduleRefresh, scrollToItem]);

  if (!isVisible) {
    return null;
  }

  return (
    <ModuleElementDiv className={props.className ?? ''} $styleText={props.styleText} style={props.style}>
      <DataElementContext.Provider value={contextValue}>{componentProps.children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default ScrollSpy;
